/**
 * A document's right-click menu: send the agent to work on it — once, until it is done, or on a
 * schedule. Opens the same dialog as the Work screen, with the document already chosen. And pin it
 * as an instruction every agent session starts with, or unpin it.
 */

import { CalendarClock, Pin, PinOff, Play, Repeat, Users } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { useFloating } from './useFloating'
import type { WorkOrderKind } from '../lib/types'

export function DocWorkMenu({
  name,
  x,
  y,
  onPick,
  onClose,
  pinned,
  onTogglePin,
}: {
  name: string
  x: number
  y: number
  onPick: (kind: WorkOrderKind) => void
  onClose: () => void
  /** Whether the document is a pinned instruction; with onTogglePin, the menu offers to change it. */
  pinned?: boolean
  onTogglePin?: () => void
}) {
  const { t } = useI18n()
  const { ref, position } = useFloating(x, y, onClose)

  const item = (kind: WorkOrderKind, icon: JSX.Element) => (
    <button
      type="button"
      role="menuitem"
      className="bug-menu__item"
      onClick={() => {
        onClose()
        onPick(kind)
      }}
    >
      {icon} {t(`work.menu.${kind}`)}
    </button>
  )

  return (
    <div
      ref={ref}
      className="bug-menu"
      role="menu"
      aria-label={name}
      style={{ left: position.left, top: position.top }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="bug-menu__head">
        <span className="bold small truncate">{name}</span>
      </div>
      {item('Once', <Play size={14} aria-hidden />)}
      {item('UntilDone', <Repeat size={14} aria-hidden />)}
      {item('Scheduled', <CalendarClock size={14} aria-hidden />)}
      {item('Team', <Users size={14} aria-hidden />)}
      {onTogglePin && (
        <button
          type="button"
          role="menuitem"
          className="bug-menu__item"
          onClick={() => {
            onClose()
            onTogglePin()
          }}
        >
          {pinned ? <PinOff size={14} aria-hidden /> : <Pin size={14} aria-hidden />} {pinned ? t('pinned.unpin') : t('pinned.pin')}
        </button>
      )}
    </div>
  )
}
